import { RISK_LEVELS, normalizeRiskLevel } from "./riskLevel";

/**
 * `AssetVersion/GetVulnerabilities` 的契約轉換（票 29）。
 *
 * 後端 `DcVulnerabilityDto`（snake_case）→ 看板既有的 view model
 * `{ cveId, cvss, level, isKev, affectedCount }`。UI 不改欄位名，改資料來源。
 *
 * **值語意是這支的重點**（ADR-0004 / 側欄事故的教訓）：
 * `severity` 是自由字串，而看板拿它當**查表鍵**去挑 `StatusTag` 的顏色
 * （`LEVEL_VARIANT[level]`）。對不上就是「安靜地變成預設樣式」——
 * 畫面不會壞、也不會有人發現顏色全錯。所以這裡：
 *   1. 認得的值原樣通過（8/14 smoke 實測回的是中文：「嚴重」「高」）
 *   2. 認得的英文/大小寫變體對回中文（後端哪天改回 Critical/High 不會整排失色）
 *   3. 都對不上 → 保留原值讓人看得到，並 `console.warn` 指路
 *
 * `affectedCount`（影響設備數）**後端沒有這個欄位** —— 整份 DTO 裡沒有任何
 * 「這個 CVE 影響幾台」的資訊。給 null 讓畫面顯示「—」，不要填 0：
 * 0 是一個看起來很正常的數字，會被當成「沒有設備受影響」。缺口記在票 29。
 */

/**
 * 等級對照表在 `helpers/riskLevel.js`（ADR-0007／票 21：**只能有一份**）。
 *
 * CVE 嚴重度與 EOS／EOL 風險等級是不同的概念，但值域與色階是同一套；
 * 這裡保留 `VULN_LEVELS`／`normalizeSeverity` 兩個名字只是為了呼叫端讀起來
 * 貼近它處理的東西（漏洞嚴重度），實際的表只有一份。
 */
export const VULN_LEVELS = RISK_LEVELS;
export const normalizeSeverity = normalizeRiskLevel;

/** DcVulnerabilityDto → 看板 view model */
export function vulnToView(dto = {}) {
  // 注意 `Number(null) === 0`:缺值直接 Number() 會變成「CVSS 0.0」——
  // 那在畫面上讀起來是「沒有風險」,不是「沒有分數」。空值要先擋掉。
  const rawCvss = dto.cvss_base_score;
  const cvss = rawCvss === null || rawCvss === undefined || rawCvss === ""
    ? NaN
    : Number(rawCvss);
  return {
    id: dto.id,
    cveId: dto.cve_id || "",
    cvss: Number.isFinite(cvss) ? cvss : null,
    level: normalizeSeverity(dto.severity),
    isKev: !!dto.is_kev,
    // 後端沒有這個欄位(見檔頭)。null → 畫面顯示「—」,不是 0。
    affectedCount: null,
    category: dto.category || "",
    source: dto.source || "",
    description: dto.description || "",
    publishedOn: dto.published_on_utc || null,
    kevDateAdded: dto.kev_date_added_utc || null,
    kevDueDate: dto.kev_due_date_utc || null,
    lastScrapedOn: dto.last_scraped_on_utc || null,
    // 四個外連,給明細用;缺的不留空字串鍵,免得畫面長出一顆連不到的按鈕
    links: [
      dto.cisco_url && { label: "Cisco", href: dto.cisco_url },
      dto.nvd_url && { label: "NVD", href: dto.nvd_url },
      dto.cve_org_url && { label: "CVE.org", href: dto.cve_org_url },
      dto.kev_url && { label: "KEV", href: dto.kev_url },
    ].filter(Boolean),
  };
}

/** 後端回 date-time,畫面只吃 YYYY-MM-DD;非字串（null）原樣回 null */
const dateOnly = (s) => (typeof s === "string" && s.length >= 10 ? s.slice(0, 10) : (s || null));

/**
 * 資產命中明細 `DcHardware/GetById.vulnerability_hits[]`（`DcSoftware` 同形）→ 檢視模型
 * （票 uat-round3/01；DCK-BUG-001／005）。
 *
 * **形狀抄 2026-09-07 testenv smoke**（`GetById/8`，46 筆）：
 *   `cve_id`／`severity`（嚴重|高|中|**無**）／`cvss_base_score`／`category`／`is_kev`／
 *   `kev_date_added_utc`／`published_on_utc`／`is_platform_level`／`description`／`reference_url`
 * 與弱點目錄 `DcVulnerabilityDto` 是**兩種 DTO**：命中只有一個 `reference_url`，
 * 沒有 nvd／cisco／cve.org／kev 四個 url。共用 `vulnToView` 處理等級與 CVSS 的值語意，
 * 這裡只補命中特有的欄位，並保留軟體命中表格既有的鍵
 * （`cve`／`published`／`kevLevel`／`kevDate`／`version`），兩張表一支 mapper。
 *
 * 已經是前端形狀的列（表單暫存、舊資料）原樣通過 —— 否則 mock 模式下
 * 軟體詳情的命中清單會整片被清空。
 */
export function vulnHitToView(h = {}) {
  if (h.cve && !h.cve_id) return { ...h };
  const base = vulnToView(h);
  const isKev = !!h.is_kev;
  return {
    ...base,
    cve: base.cveId,
    published: dateOnly(h.published_on_utc) || h.published || "",
    // 軟體命中表格的既有語意：是 KEV 才有 kevLevel（取這筆的 severity），不是 → null 顯示「—」。
    // severity「無」的 KEV 命中 → kevLevel 也是 null：舊 mapper 在這裡補「高」，那是前端
    // 憑空生出的等級（ADR-0007）；畫面改以 isKev 標記「KEV」，不假造等級。
    kevLevel: isKev ? (base.level || h.kevLevel || null) : null,
    // KEV 加入日照後端給的留著（含非 KEV 列）—— 與軟體命中表格原本的行為一致
    kevDate: dateOnly(h.kev_date_added_utc) || h.kevDate || null,
    // 契約沒有 affected_version —— 顯示「—」，不准拿資產本身的版本冒充
    version: h.affected_version || h.version || "",
    referenceUrl: h.reference_url || "",
    isPlatformLevel: !!h.is_platform_level,
  };
}

/**
 * 等級彙總（看板標題列那四顆 chip）。
 *
 * 舊版是 `mock/db/vulns.js` 裡一個**寫死的物件**（嚴重 18／高 55／中 18／低 41），
 * 跟同一頁列出的六筆資料對不上 —— 換成從實際清單算。四個等級一律都在
 * （沒有的給 0），否則 chip 會時有時無，版面跳動。
 */
export function vulnLevelSummary(list = []) {
  const acc = VULN_LEVELS.reduce((m, lv) => ({ ...m, [lv]: 0 }), {});
  list.forEach((v) => {
    if (acc[v.level] !== undefined) acc[v.level] += 1;
  });
  return acc;
}

/**
 * 「這份資料多新」—— 取整份清單裡最後一次爬取時間。
 *
 * 存在的理由：看板上「CVE 0 種」有兩種完全相反的意思 —— 真的沒有漏洞，
 * 或爬蟲掛了三天沒跑。沒有這行字的話，兩者在畫面上長得一模一樣。
 *
 * 這是**退而求其次**的來源（`DcVulnerabilityDto` 自帶的欄位）：它只說得出
 * 「資料最後被抓到是什麼時候」，說不出「最後一次跑成功了沒」。正式的執行紀錄
 * 走 `scraperRunToView`（8/17 規格書補上 DTO 後才能用）。
 */
export function latestScrapedAt(list = []) {
  return list.reduce((latest, v) => {
    if (!v || !v.lastScrapedOn) return latest;
    if (!latest) return v.lastScrapedOn;
    return new Date(v.lastScrapedOn) > new Date(latest) ? v.lastScrapedOn : latest;
  }, null);
}

/**
 * `DcScraperRunDto` → 爬蟲執行紀錄（票 29 的第二個缺口，8/17 規格書補上 DTO 後補完）。
 *
 * 8/16 當時這支只有端點沒有 DTO，我照文件猜了欄位名又拿掉 —— 那正是 ADR-0004
 * 要防的事。現在契約明確了：`success` 是 **boolean**，不是狀態字串，
 * 所以不需要「哪些字算成功」那種對照表（原本猜的 `status` 欄根本不存在）。
 *
 * `items_failed > 0` 但 `success = true` 是可能的（部分未匹配仍算跑完），
 * 所以「有沒有跑成功」與「有沒有東西沒對上」是兩件事，畫面要分開講。
 */
export function scraperRunToView(dto = {}) {
  return {
    id: dto.id,
    source: dto.source || "",
    startedOn: dto.started_on_utc || null,
    finishedOn: dto.finished_on_utc || null,
    // 還在跑的時候 finished 是 null —— 那不是失敗,是「進行中」
    running: !!dto.started_on_utc && !dto.finished_on_utc,
    success: !!dto.success,
    processed: dto.items_processed ?? 0,
    updated: dto.items_updated ?? 0,
    failed: dto.items_failed ?? 0,
    message: dto.message || "",
  };
}

/**
 * 執行紀錄 → 看板要講的那一句話。
 *
 * 規則刻意分三種,因為它們要使用者做的事不一樣:
 *   進行中  → 什麼都不用做,等
 *   失敗    → 資料可能是舊的,要找人看爬蟲
 *   成功但有沒對上的 → 資料是新的,但有幾筆資產沒被更新到
 */
export function scraperStatusLine(runs = []) {
  const latest = runs.find((r) => r && (r.startedOn || r.finishedOn));
  if (!latest) return null;
  const when = latest.finishedOn || latest.startedOn;
  if (latest.running) return { tone: "info", text: `爬蟲執行中（${latest.source || "來源未標示"}）` };
  if (!latest.success) {
    return {
      tone: "danger",
      text: `最後一次爬取失敗${latest.message ? `：${latest.message}` : ""} —— 以下數字可能不是最新的`,
      at: when,
    };
  }
  if (latest.failed > 0) {
    return {
      tone: "warning",
      text: `資料已更新，但有 ${latest.failed} 筆沒有對應到系統中的資產`,
      at: when,
    };
  }
  return { tone: "muted", text: "資料更新於", at: when };
}
